import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from "../../useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import _ from 'lodash';
import { useToast } from "../../../contexts/ToastContext";
import { determineInvoiceStatus } from '../../../Utils/purchaseInvoices/invoiceStatus';
import {
    INVOICES_QUERY_KEY,
    APPROVAL_PACKAGES_QUERY_KEY,
    INVOICE_STATUS_CHANGED_EVENT,
    ACCOUNTANT_NAMES_CACHE_KEY,
    INVOICE_STATUS_PROCESSING,
    INVOICE_STATUS_PROCESSING_HE,
    INVOICE_STATUS_APPROVED,
    INVOICE_STATUS_REJECTED,
    INVOICES_STALE_TIME_MS,
    INVOICES_GC_TIME_MS,
    INVOICES_REFETCH_INTERVAL_MS,
    INVOICES_PRIORITY_STALE_TIME_MS,
    INVOICES_PRIORITY_REFETCH_INTERVAL_MS,
    MIN_VISIBILITY_REFETCH_INTERVAL_MS,
    FILE_STALE_TIME_MS,
    FILE_GC_TIME_MS,
    POST_ACTION_REFRESH_DELAY_MS,
    STATUS_CHANGE_INVALIDATE_DELAY_MS,
    PROCESSING_POLL_MAX_COUNT,
    PROCESSING_POLL_MAX_DURATION_MS,
    PROCESSING_POLL_FAST_THRESHOLD,
    PROCESSING_POLL_MEDIUM_THRESHOLD,
    PROCESSING_POLL_SLOW_THRESHOLD,
    PROCESSING_POLL_VERY_SLOW_THRESHOLD,
    PROCESSING_POLL_INTERVAL_FAST_MS,
    PROCESSING_POLL_INTERVAL_MEDIUM_MS,
    PROCESSING_POLL_INTERVAL_SLOW_MS,
    PROCESSING_POLL_INTERVAL_VERY_SLOW_MS,
    PROCESSING_POLL_INTERVAL_MINIMUM_MS,
} from '../../../constants/purchaseInvoices/supplierInvoicesConstants';

const useSpInvoices = () => {
    const [viewAll, setViewAll] = useState(false);
    const viewAllRef = useRef(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [allocationKeyModalOpen, setAllocationKeyModalOpen] = useState(false);
    const [allocationKeys, setAllocationKeys] = useState([]);
    const [isLoadingAllocationKeys, setIsLoadingAllocationKeys] = useState(false);
    const [allocationKeysError, setAllocationKeysError] = useState(null);
    const [pendingApprovalPackageId, setPendingApprovalPackageId] = useState(null);
    const [currentInvoiceYear, setCurrentInvoiceYear] = useState('');
    const accountantNamesCacheRef = useRef((() => {
        try {
            const cached = localStorage.getItem(ACCOUNTANT_NAMES_CACHE_KEY);
            return cached ? JSON.parse(cached) : {};
        } catch {
            return {};
        }
    })());

    const updateCache = useCallback((newCache) => {
        accountantNamesCacheRef.current = newCache;
        try {
            localStorage.setItem(ACCOUNTANT_NAMES_CACHE_KEY, JSON.stringify(newCache));
        } catch {
            // silent fail - localStorage may be unavailable
        }
    }, []);

    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.read,
    });

    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const fetchAccountantDisplayName = useCallback(async (priorityUsername) => {
        if (!priorityUsername) return '';
        if (accountantNamesCacheRef.current[priorityUsername]) {
            return accountantNamesCacheRef.current[priorityUsername];
        }
        try {
            const response = await execute(
                'GET',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getNameAccountingFirm/${priorityUsername}`
            );
            const displayName = response.data?.displayName || priorityUsername;
            updateCache({ ...accountantNamesCacheRef.current, [priorityUsername]: displayName });
            return displayName;
        } catch {
            updateCache({ ...accountantNamesCacheRef.current, [priorityUsername]: priorityUsername });
            return priorityUsername;
        }
    }, [execute, updateCache]);

    const getAccountantDisplayName = useCallback((priorityUsername) => {
        if (!priorityUsername) return '-';
        if (accountantNamesCacheRef.current[priorityUsername]) {
            return accountantNamesCacheRef.current[priorityUsername];
        }
        fetchAccountantDisplayName(priorityUsername).catch(() => { });
        return priorityUsername;
    }, [fetchAccountantDisplayName]);

    const fetchMultipleAccountantNames = useCallback(async (priorityUsernames) => {
        const uniqueUsernames = [...new Set(priorityUsernames.filter(Boolean))];
        const uncachedUsernames = uniqueUsernames.filter(username => !accountantNamesCacheRef.current[username]);
        if (uncachedUsernames.length === 0) return;

        try {
            const response = await execute(
                'POST',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getMultipleNamesAccountingFirm`,
                { usernames: uncachedUsernames }
            );
            const newCache = { ...accountantNamesCacheRef.current };
            if (response?.status === 'SUCCESS' && response?.data) {
                Object.keys(response.data).forEach(username => {
                    const userData = response.data[username];
                    newCache[username] = userData?.displayName || username;
                });
            } else {
                uncachedUsernames.forEach(username => {
                    if (!newCache[username]) newCache[username] = username;
                });
            }
            updateCache(newCache);
        } catch {
            const newCache = { ...accountantNamesCacheRef.current };
            uncachedUsernames.forEach(username => {
                if (!newCache[username]) newCache[username] = username;
            });
            updateCache(newCache);
        }
    }, [execute, updateCache]);

    useEffect(() => {
        viewAllRef.current = viewAll;
    }, [viewAll]);

    useEffect(() => {
        const handleStatusChange = (event) => {
            if (event.detail?.invoiceId && event.detail?.newStatus) {
                const applyUpdate = (data) =>
                    (data || []).map(inv =>
                        inv.id === event.detail.invoiceId
                            ? { ...inv, status: event.detail.newStatus }
                            : inv
                    );
                for (const va of [false, true]) {
                    const key = [INVOICES_QUERY_KEY, 'all', va];
                    const current = queryClient.getQueryData(key);
                    if (current) queryClient.setQueryData(key, applyUpdate(current));
                }
                queryClient.setQueryData(
                    [INVOICES_QUERY_KEY, 'priority'],
                    applyUpdate(queryClient.getQueryData([INVOICES_QUERY_KEY, 'priority']))
                );
            }
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY],
                    refetchType: 'none'
                });
            }, STATUS_CHANGE_INVALIDATE_DELAY_MS);
        };

        window.addEventListener(INVOICE_STATUS_CHANGED_EVENT, handleStatusChange);
        return () => {
            window.removeEventListener(INVOICE_STATUS_CHANGED_EVENT, handleStatusChange);
        };
    }, [queryClient]);

    const mapInvoicesToDisplayFormat = useCallback((response) => {
        return response.map(i => {
            const status = determineInvoiceStatus(i, i.approvalPackage);
            let sortableUploadedAt = null;
            let sortableIvDate = null;
            if (i.uploadedAt) {
                sortableUploadedAt = new Date(i.uploadedAt).getTime();
            } else if (i.ivDate) {
                const parts = i.ivDate.split('/');
                if (parts.length === 3)
                    sortableUploadedAt = new Date(parts[2], parts[1] - 1, parts[0]).getTime();
            }
            if (i.ivDate) {
                const parts = i.ivDate.split('/');
                if (parts.length === 3)
                    sortableIvDate = new Date(parts[2], parts[1] - 1, parts[0]).getTime();
            }
            return {
                id: i._id,
                booknum: i.booknum,
                orderName: i.orderName,
                date: i.ivDate,
                dateForSorting: sortableIvDate,
                uploadedAtForSorting: sortableUploadedAt,
                supplierName: i.supName,
                supNumber: i.supNumber || '',
                companyName: i.companyName || '',
                companyId: i.companyId || '',
                amount: i.totals?.total != null ? parseFloat(i.totals.total?.$numberDecimal ?? i.totals.total) : null,
                amountBeforeVat: i.totals?.subtotal != null ? parseFloat(i.totals.subtotal?.$numberDecimal ?? i.totals.subtotal) : null,
                filePath: i.filePath,
                fileName: i.fileName,
                status,
                approvalPackage: i.approvalPackage || '',
                accountManager: i.approvalPackage?.accountManager,
                approver: i?.approvalPackage?.approversList?.at(-1)?.fullName
                    || i?.approvalPackage?.approversList?.at(-1)?.userName || '-',
                approvedBy: i.approvedBy,
                approvedAt: i.approvedAt,
                autoApproved: i.autoApproved,
                autoApprovalReason: i.autoApprovalReason,
                autoApprovalDetails: i.autoApprovalDetails,
                rejectedBy: i.rejectedBy,
                rejectedAt: i.rejectedAt,
                rejectionReason: i.rejectionReason || i?.approvalPackage?.rejection?.reason,
                rejection: i?.approvalPackage?.rejection,
                allocationNumber: i.allocationNumber,
                notes: i.notes || '',
                uploadedAt: i.uploadedAt,
                multipleOrders: i.approvalPackage?.order?.MULTIPLE_ORDERS,
                allOrders: i.approvalPackage?.order?.ALL_ORDERS,
                errorDetails: i.errorDetails,
                currencyCode: i.currencyCode,
                groupSettings: i.groupSettings || null,
                groupId: i.groupSettings?._id || i.groupSettings || i.groupId || null
            };
        });
    }, []);

    const fetchPriorityInvoices = useCallback(async () => {
        try {
            const response = await execute(
                'GET',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices?priority=true`
            );
            if (!response) throw new Error('Failed to fetch priority invoices');
            return mapInvoicesToDisplayFormat(response);
        } catch (error) {
            console.error('Error fetching priority invoices:', error);
            throw error;
        }
    }, [execute, mapInvoicesToDisplayFormat]);

    const fetchInvoices = useCallback(async () => {
        try {
            const url = viewAllRef.current
                ? `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices?viewAll=true`
                : `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices`;
            const response = await execute("GET", url);
            if (!response) throw new Error('Failed to fetch invoices');
            const invoicesWithStatus = mapInvoicesToDisplayFormat(response);
            const accountantUsernames = [...new Set(
                invoicesWithStatus.map(inv => inv.accountManager).filter(Boolean)
            )];
            if (accountantUsernames.length > 0) {
                fetchMultipleAccountantNames(accountantUsernames).catch(() => { });
            }
            return invoicesWithStatus;
        } catch (error) {
            console.error('Error fetching invoices:', error);
            throw error;
        }
    }, [execute, mapInvoicesToDisplayFormat, fetchMultipleAccountantNames]);

    const fetchAllocationKeys = async () => {
        setIsLoadingAllocationKeys(true);
        setAllocationKeysError(null);
        try {
            const response = await execute(
                "GET",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getAllocationKeys`
            );
            const keysArray = Array.isArray(response) ? response :
                (Array.isArray(response.data) ? response.data : []);
            setAllocationKeys(keysArray);
        } catch (error) {
            console.error("שגיאה בטעינת מפתחות העמסה:", error);

            if (error.response?.status === 403) {
                setAllocationKeysError("אין לך הרשאה לטעינת מפתחות העמסה");
                showToast("אין לך הרשאה לטעינת מפתחות העמסה", "error");
            } else {
                setAllocationKeysError(error.message || "שגיאה בטעינת מפתחות העמסה");
            }
        } finally {
            setIsLoadingAllocationKeys(false);
        }
    };

    const isInvoicePriceHigher = (invoice) => {
        return !!invoice?.errorDetails;
    };

    const initiateApproval = (invoice) => {
        if (invoice.errorDetails) {
            const errorMessage = invoice.errorDetails.tooltip ||
                invoice.errorDetails.hebrewMessage ||
                'החשבונית דורשת תשומת לב';
            showToast(`לא ניתן לאשר - ${errorMessage}`, 'error');
            return;
        }

        let invoiceYear = '';
        if (invoice.date) {
            const dateParts = invoice.date.split('/');
            if (dateParts.length === 3) {
                invoiceYear = dateParts[2];
            }
        }

        setPendingApprovalPackageId(invoice.approvalPackage?._id);
        setCurrentInvoiceYear(invoiceYear);
        setAllocationKeyModalOpen(true);
        fetchAllocationKeys();
    };

    const completeApprovalWithAllocationKey = async (selectedKey, onClosePopup = null) => {
        try {
            if (!pendingApprovalPackageId) {
                throw new Error("Missing approval package ID");
            }

            setAllocationKeyModalOpen(false);
            if (onClosePopup) onClosePopup();

            showToast('החשבונית אושרה בהצלחה', 'success');

            const updatedInvoices = spInvoices.map(inv =>
                inv.approvalPackage?._id === pendingApprovalPackageId
                    ? {
                        ...inv,
                        status: INVOICE_STATUS_APPROVED,
                        approvedAt: new Date().toISOString()
                    }
                    : inv
            );
            setActiveInvoicesData(updatedInvoices);

            const invoiceId = spInvoices.find(inv => inv.approvalPackage?._id === pendingApprovalPackageId)?.id;
            if (invoiceId) {
                const event = new CustomEvent(INVOICE_STATUS_CHANGED_EVENT, {
                    detail: { invoiceId, newStatus: INVOICE_STATUS_APPROVED }
                });
                window.dispatchEvent(event);
            }

            let payload = {};
            if (selectedKey?.type === 'percentage_allocation') {
                payload = {
                    percentageAllocations: selectedKey.allocations,
                    allocationType: 'percentage_allocation'
                };
            } else if (selectedKey?.isNoKey) {
                payload = {
                    allocationKey: {
                        name: 'NO_KEY',
                        isNoKey: true
                    },
                    allocationType: 'no_allocation_key'
                };
            } else if (selectedKey?.splitKeyName || selectedKey?.name) {
                payload = {
                    allocationKey: {
                        name: selectedKey.name || selectedKey.splitKeyName,
                        splitKeyName: selectedKey.splitKeyName,
                        id: selectedKey.id || null
                    },
                    allocationType: 'allocation_key'
                };
            }

            execute(
                'PATCH',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/approvalConfirmation/${pendingApprovalPackageId}`,
                payload
            ).then(response => {
                if (response?.status === 'ERROR') {
                    console.error('Background approval error:', response.message);
                }
            }).catch(error => {
                console.error('Background approval error:', error);
            });

            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY],
                    refetchType: 'none'
                });
                queryClient.invalidateQueries({
                    queryKey: [APPROVAL_PACKAGES_QUERY_KEY],
                    refetchType: 'none'
                });
            }, POST_ACTION_REFRESH_DELAY_MS);
        } catch (error) {
            console.error('Error completing approval with allocation key:', error);

            if (error.message && error.message.includes("Missing approval package ID")) {
                const errorMessage = "שגיאה באישור החשבונית - חסרים נתונים";
                showToast(errorMessage, "error");
            } else {
                console.error('Background allocation key approval error - logged for ServiceBus retry:', error);
            }
        } finally {
            setPendingApprovalPackageId(null);
        }
    };

    const saveInvoiceNotesToPriority = async (invoiceId, notes, isReplace = false) => {
        try {
            const response = await execute(
                'PATCH',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/priority/invoices/${invoiceId}/notes`,
                { notes, isReplace }
            );
            return response;
        } catch (error) {
            console.error('Error saving notes:', error);
            throw error;
        }
    };

    const approveInvoice = async (approvalPackageId, allocationKeyName) => {
        try {
            if (!approvalPackageId) {
                throw new Error("נדרש מזהה חבילת אישור");
            }

            const response = await execute(
                'PATCH',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/approvalConfirmation/${approvalPackageId}`,
                { allocationKeyName }
            );

            if (!response) {
                throw new Error("לא התקבלה תשובה מהשרת");
            }

            return response;
        } catch (error) {
            console.error('Error approving invoice:', error);
            throw error;
        }
    };

    const rejectInvoice = async (rejectionData) => {
        try {
            if (!rejectionData || !rejectionData.apId) {
                throw new Error("נדרש מזהה חבילת אישור");
            }

            if (!rejectionData.rejectionReason) {
                throw new Error("נדרשת סיבת דחייה");
            }

            const response = await execute(
                'POST',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/approvalRejection`,
                { rejectionData }
            );

            if (!response) {
                throw new Error("לא התקבלה תשובה מהשרת");
            }

            return response;
        } catch (error) {
            console.error('Error rejecting invoice:', error);
            throw error;
        }
    };

    const fetchApprovalPackageData = async (approvalPackageId) => {
        try {
            const response = await execute(
                'GET',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getApprovalPackageById/${approvalPackageId}`
            );
            if (response && response.status === 'SUCCESS' && response.data && response.data.approvalPackage) {
                return response.data.approvalPackage;
            } else {
                return response;
            }
        } catch (error) {
            console.error('Error fetching approval package data:', error);
            throw error;
        }
    };

    const deleteInvoice = async () => {
        const response = await execute(
            'DELETE',
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${selectedFileName}`
        );
        if (!response) throw new Error('Failed to delete invoice');
    };

    const updateInvoice = async (invoiceData) => {
        const response = await execute(
            'PUT',
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/update`,
            invoiceData
        );
        if (!response) throw new Error('Failed to update invoice');
        return response;
    };

    const checkGoodsReceiptNow = async (id) => {
        try {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${id}/check-goods-receipt`
            );
            return response;
        } catch (error) {
            throw error;
        }
    };

    const retryApprovedInvoiceToPriority = async (invoiceId) => {
        try {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/retry-approved-to-priority/${invoiceId}`
            );
            return response;
        } catch (error) {
            throw error;
        }
    };

    const retryRequiresAttentionInvoiceToPriority = async (invoiceId) => {
        return execute(
            "POST",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/retry-requires-attention-to-priority/${invoiceId}`
        );
    };

    const reprocessRequiresAttentionInvoice = async (invoiceId, updatedFields) => {
        try {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${invoiceId}/reprocess`,
                updatedFields
            );

            if (response?.status === 'SUCCESS' || response?.status === 'PENDING') {
                showToast(response.message || 'חשבונית עובדה בהצלחה', "success");
                await queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
                return response;
            } else {
                throw new Error(response?.message || 'שגיאה בעיבוד החשבונית');
            }
        } catch (error) {
            console.error('Error reprocessing requires attention invoice:', error);
            const errorMessage = error.response?.data?.message || error.message || 'שגיאה בעיבוד מחדש של החשבונית';
            showToast(errorMessage, "error");
            throw error;
        }
    };

    const changeInvoiceStatus = async (invoiceId, newStatus) => {
        try {
            const response = await execute(
                "PATCH",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/change-status/${invoiceId}`,
                { status: newStatus }
            );
            return response;
        } catch (error) {
            throw error;
        }
    };

    const { data: viewAllEligibleData } = useQuery({
        queryKey: ['view-all-eligible'],
        queryFn: async () => execute(
            'GET',
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/view-all-eligible`
        ),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const canViewAll = !!viewAllEligibleData?.eligible;

    const {
        data: allInvoices = [],
        isLoading: isLoadingAll,
        isSuccess: isAllLoaded,
        error: allError,
        refetch: refetchAll
    } = useQuery({
        queryKey: [INVOICES_QUERY_KEY, 'all', viewAll],
        queryFn: fetchInvoices,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: INVOICES_STALE_TIME_MS,
        gcTime: INVOICES_GC_TIME_MS,
        refetchInterval: INVOICES_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false
    });

    const {
        data: priorityInvoices = [],
        isLoading: isPriorityLoading,
        error: priorityError,
        refetch: refetchPriority
    } = useQuery({
        queryKey: [INVOICES_QUERY_KEY, 'priority'],
        queryFn: fetchPriorityInvoices,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: INVOICES_PRIORITY_STALE_TIME_MS,
        gcTime: INVOICES_GC_TIME_MS,
        refetchInterval: isAllLoaded ? false : INVOICES_PRIORITY_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false
    });

    const spInvoices = useMemo(() => {
        if (isAllLoaded) return allInvoices;
        return priorityInvoices;
    }, [isAllLoaded, allInvoices, priorityInvoices]);

    // Updates both caches so optimistic updates are visible regardless of which is currently active.
    const setActiveInvoicesData = useCallback((updatedInvoices) => {
        queryClient.setQueryData([INVOICES_QUERY_KEY, 'all', viewAllRef.current], updatedInvoices);
        if (!isAllLoaded) {
            queryClient.setQueryData([INVOICES_QUERY_KEY, 'priority'], updatedInvoices);
        }
    }, [queryClient, isAllLoaded]);

    const isLoading = isPriorityLoading;
    const isLoadingBackground = !isAllLoaded;
    const error = priorityError || allError;

    const refetch = useCallback(() => {
        if (!isAllLoaded) refetchPriority();
        refetchAll();
    }, [isAllLoaded, refetchPriority, refetchAll]);

    const lastRefetchRef = useRef(0);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now();
                if (now - lastRefetchRef.current > MIN_VISIBILITY_REFETCH_INTERVAL_MS) {
                    lastRefetchRef.current = now;
                    refetch();
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [refetch]);

    useEffect(() => {
        const processingInvoices = spInvoices?.filter(
            invoice => invoice.status === INVOICE_STATUS_PROCESSING || invoice.status === INVOICE_STATUS_PROCESSING_HE
        ) || [];

        if (processingInvoices.length === 0) {
            return;
        }

        let pollCount = 0;
        const maxPolls = PROCESSING_POLL_MAX_COUNT;
        const startTime = Date.now();
        const maxDuration = PROCESSING_POLL_MAX_DURATION_MS;

        const getPollingInterval = (count) => {
            if (count < PROCESSING_POLL_FAST_THRESHOLD) return PROCESSING_POLL_INTERVAL_FAST_MS;
            if (count < PROCESSING_POLL_MEDIUM_THRESHOLD) return PROCESSING_POLL_INTERVAL_MEDIUM_MS;
            if (count < PROCESSING_POLL_SLOW_THRESHOLD) return PROCESSING_POLL_INTERVAL_SLOW_MS;
            if (count < PROCESSING_POLL_VERY_SLOW_THRESHOLD) return PROCESSING_POLL_INTERVAL_VERY_SLOW_MS;
            return PROCESSING_POLL_INTERVAL_MINIMUM_MS;
        };

        const schedulePoll = () => {
            const interval = getPollingInterval(pollCount);
            const timeoutId = setTimeout(() => {
                pollCount++;
                const elapsed = Date.now() - startTime;
                if (pollCount >= maxPolls || elapsed >= maxDuration) {
                    return;
                }
                const stillProcessing = spInvoices?.some(
                    invoice => invoice.status === INVOICE_STATUS_PROCESSING || invoice.status === INVOICE_STATUS_PROCESSING_HE
                );

                if (stillProcessing) {
                    refetch();
                    schedulePoll();
                }
            }, interval);

            return timeoutId;
        };

        const timeoutId = schedulePoll();
        return () => clearTimeout(timeoutId);
    }, [spInvoices, refetch]);

    const deleteMutation = useMutation({
        mutationFn: deleteInvoice,
        onSuccess: () => {
            showToast("החשבונית נמחקה בהצלחה", "success");
            setOpenDelete(false);
            queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
        },
        onError: (err) => {
            if (err.response?.status === 403) {
                showToast("אין לך הרשאה למחוק חשבונית זו", "error");
            } else if (err.response?.status === 404) {
                showToast("החשבונית לא נמצאה", "error");
            } else {
                showToast("שגיאה במחיקה", "error");
            }
        }
    });

    const updateMutation = useMutation({
        mutationFn: updateInvoice,
        onSuccess: () => {
            showToast("החשבונית עודכנה בהצלחה", "success");
            queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
        },
        onError: (err) => {
            if (err.response?.status === 403) {
                showToast("אין לך הרשאה לעדכן חשבונית זו", "error");
            } else if (err.response?.status === 404) {
                showToast("החשבונית לא נמצאה", "error");
            } else {
                showToast("שגיאה בעדכון החשבונית", "error");
            }
        }
    });

    const saveNotesMutation = useMutation({
        mutationFn: ({ invoiceId, notes, isReplace = false }) => saveInvoiceNotesToPriority(invoiceId, notes, isReplace),
        onSuccess: (_, variables) => {
            const message = variables.isReplace
                ? 'ההערה נמחקה בהצלחה'
                : 'הערות נשמרו בהצלחה';
            showToast(message, 'success');
            queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
        },
        onError: (error, variables) => {
            console.error('Error saving notes:', error);
            const message = variables.isReplace
                ? 'שגיאה במחיקת ההערה'
                : 'שגיאה בשמירת הערות';
            showToast(message, 'error');
            throw error;
        }
    });

    const approveMutation = useMutation({
        mutationFn: ({ approvalPackageId, allocationKeyName }) => {
            return approveInvoice(approvalPackageId, allocationKeyName);
        },
        onSuccess: () => {
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY],
                    refetchType: 'none'
                });
                queryClient.invalidateQueries({
                    queryKey: [APPROVAL_PACKAGES_QUERY_KEY],
                    refetchType: 'none'
                });
            }, POST_ACTION_REFRESH_DELAY_MS);
        },
        onError: (error) => {
            console.error('Background approval error:', error);
        }
    });

    const rejectMutation = useMutation({
        mutationFn: rejectInvoice,
        onSuccess: () => {
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY],
                    refetchType: 'none'
                });
                queryClient.invalidateQueries({
                    queryKey: [APPROVAL_PACKAGES_QUERY_KEY],
                    refetchType: 'none'
                });
            }, POST_ACTION_REFRESH_DELAY_MS);
        },
        onError: (error) => {
            console.error('Background rejection error:', error);
        }
    });

    const handleSaveInvoiceNotes = async (invoice, notes, isReplace = false) => {
        try {
            if (!invoice.id) {
                throw new Error("לא נמצא מזהה לחשבונית - לא ניתן לשמור הערות");
            }

            const result = await saveNotesMutation.mutateAsync({
                invoiceId: invoice.id,
                notes,
                isReplace
            });

            if (result && result.success) {
                const updatedInvoices = spInvoices.map(inv => {
                    if (inv.id === invoice.id) {
                        return {
                            ...inv,
                            notes: result.notesData || inv.notes
                        };
                    }
                    return inv;
                });

                setActiveInvoicesData(updatedInvoices);
            }

        } catch (error) {
            console.error("Error saving notes:", error);
            throw error;
        }
    };

    const handleApproveInvoiceFromPopup = async (invoiceId, allocationKeyName, onClosePopup) => {
        const snapshotAll = queryClient.getQueryData([INVOICES_QUERY_KEY, 'all']);
        const snapshotPriority = queryClient.getQueryData([INVOICES_QUERY_KEY, 'priority']);
        try {
            const invoice = spInvoices.find(inv => inv.id === invoiceId);
            if (!invoice) {
                throw new Error("לא נמצאה חשבונית תואמת למזהה שהתקבל");
            }

            const approvalPackageId = invoice?.approvalPackage?._id;
            if (!approvalPackageId) {
                throw new Error("לא נמצאה חבילת אישור לחשבונית זו");
            }

            showToast('החשבונית אושרה בהצלחה', 'success');
            if (onClosePopup) onClosePopup();

            const updatedInvoices = spInvoices.map(inv =>
                inv.id === invoiceId
                    ? {
                        ...inv,
                        status: INVOICE_STATUS_APPROVED,
                        approvedAt: new Date().toISOString()
                    }
                    : inv
            );
            setActiveInvoicesData(updatedInvoices);

            const event = new CustomEvent(INVOICE_STATUS_CHANGED_EVENT, {
                detail: { invoiceId, newStatus: INVOICE_STATUS_APPROVED }
            });
            window.dispatchEvent(event);

            let payload = {};
            if (allocationKeyName?.type === 'percentage_allocation') {
                payload = {
                    percentageAllocations: allocationKeyName.allocations,
                    allocationType: 'percentage_allocation'
                };
            } else if (allocationKeyName?.isNoKey) {
                payload = {
                    allocationKey: {
                        name: 'NO_KEY',
                        isNoKey: true
                    },
                    allocationType: 'no_allocation_key'
                };
            } else if (allocationKeyName) {
                payload = {
                    allocationKey: {
                        name: typeof allocationKeyName === 'string' ? allocationKeyName : allocationKeyName.name,
                        splitKeyName: typeof allocationKeyName === 'object' ? allocationKeyName.splitKeyName : null,
                        id: allocationKeyName.id || null
                    },
                    allocationType: 'allocation_key'
                };
            }

            execute(
                'PATCH',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/approvalConfirmation/${approvalPackageId}`,
                payload
            ).then(response => {
                if (response?.status === 'ERROR') {
                    console.error('Background approval error:', response.message);
                }
            }).catch(error => {
                console.error('Background approval error:', error);
            });

            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY],
                    refetchType: 'none'
                });
                queryClient.invalidateQueries({
                    queryKey: [APPROVAL_PACKAGES_QUERY_KEY],
                    refetchType: 'none'
                });
            }, POST_ACTION_REFRESH_DELAY_MS);
        } catch (error) {
            console.error('Error in handleApproveInvoiceFromPopup:', error);

            if (error.response?.status === 403) {
                const errorMessage = "אין לך הרשאה לאשר חשבונית זו";
                showToast(errorMessage, "error");
                queryClient.setQueryData([INVOICES_QUERY_KEY, 'all'], snapshotAll);
                queryClient.setQueryData([INVOICES_QUERY_KEY, 'priority'], snapshotPriority);
            } else {
                console.error('Background approval error - logged for ServiceBus retry:', error);
            }
        }
    };

    const handleRejectInvoiceFromPopup = async (invoiceId, rejectionData, onClosePopup) => {
        const snapshotAll = queryClient.getQueryData([INVOICES_QUERY_KEY, 'all']);
        const snapshotPriority = queryClient.getQueryData([INVOICES_QUERY_KEY, 'priority']);
        try {
            let processedRejectionData;

            if (typeof rejectionData === 'string') {
                if (!rejectionData || !rejectionData.trim()) {
                    throw new Error("נדרשת סיבת דחייה");
                }

                const invoice = spInvoices.find(inv => inv.id === invoiceId);
                if (!invoice) {
                    throw new Error("לא נמצאה חשבונית תואמת למזהה שהתקבל");
                }

                const approvalPackageId = invoice?.approvalPackage?._id;
                if (!approvalPackageId) {
                    throw new Error("לא נמצאה חבילת אישור לחשבונית זו");
                }

                processedRejectionData = {
                    apId: approvalPackageId,
                    rejectionReason: rejectionData,
                    rejectionText: rejectionData
                };
            } else {
                processedRejectionData = rejectionData;

                if (!processedRejectionData.apId) {
                    throw new Error("חסר מזהה חבילת אישור בנתוני הדחייה");
                }

                if (!processedRejectionData.rejectionReason && !processedRejectionData.rejectionText) {
                    throw new Error("חסרה סיבת דחייה");
                }
            }

            showToast('החשבונית נדחתה בהצלחה', 'success');
            if (onClosePopup) onClosePopup();

            const updatedInvoices = spInvoices.map(inv =>
                inv.id === invoiceId
                    ? {
                        ...inv,
                        status: INVOICE_STATUS_REJECTED,
                        rejectedAt: new Date().toISOString(),
                        rejectionReason: processedRejectionData.rejectionReason
                    }
                    : inv
            );
            setActiveInvoicesData(updatedInvoices);

            const event = new CustomEvent(INVOICE_STATUS_CHANGED_EVENT, {
                detail: { invoiceId, newStatus: INVOICE_STATUS_REJECTED }
            });
            window.dispatchEvent(event);

            rejectMutation.mutateAsync(processedRejectionData).catch(error => {
                console.error('Background rejection error:', error);
            });
        } catch (error) {
            console.error('Error in handleRejectInvoiceFromPopup:', error);
            if (error.response?.status === 403) {
                const errorMessage = "אין לך הרשאה לדחות חשבונית זו";
                showToast(errorMessage, "error");
                queryClient.setQueryData([INVOICES_QUERY_KEY, 'all'], snapshotAll);
                queryClient.setQueryData([INVOICES_QUERY_KEY, 'priority'], snapshotPriority);
            } else {
                console.error('Background rejection error - logged for ServiceBus retry:', error);
            }
        }
    };

    const handleRowClick = async (invoice, setters) => {
        const {
            setSelectedInvoiceForPopup,
            setApprovalPackageForPopup,
            setOpenInvoicePopup
        } = setters;

        const completeInvoice = { ...invoice };
        setSelectedInvoiceForPopup(completeInvoice);

        if ((invoice.status === 'ממתין לאישור' ||
            invoice.status === INVOICE_STATUS_APPROVED ||
            invoice.status === INVOICE_STATUS_REJECTED ||
            invoice.status === 'REQUIRES_ATTENTION' ||
            invoice.status === 'חשבונית להמשך טיפול' ||
            invoice.status === 'PENDING_RETAINER_ALLOCATION' ||
            invoice.status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
            invoice.status === 'PENDING_CLARIFICATION' ||
            invoice.isRetainerInvoice ||
            invoice.approvedBy ||
            invoice.rejectedBy) &&
            invoice.approvalPackage?._id) {
            try {
                const approvalData = await fetchApprovalPackageData(invoice.approvalPackage._id);
                setApprovalPackageForPopup(approvalData);
            } catch (error) {
                console.error('Error fetching approval package data:', error);
                setApprovalPackageForPopup(null);
            }
        } else {
            setApprovalPackageForPopup(null);
        }

        if (invoice.fileName) {
            setSelectedFileName(invoice.fileName);
        }

        setOpenInvoicePopup(true);
    };

    const showDeleteComp = (value) => {
        setOpenDelete(value);
    };

    const statuses = useMemo(() => {
        const uniqueStatuses = _.uniq(spInvoices.map(item => item.status)).filter(Boolean);
        return ['all', ...uniqueStatuses];
    }, [spInvoices]);

    const filteredspInvoices = useMemo(() => {
        if (selectedStatus === 'all') {
            return spInvoices.filter(item => {
                const status = (item.status || '').trim();
                return status !== 'אושר ונקלט' && status !== 'נסגרה בפריוריטי'
                    && status.toLowerCase() !== 'cancelled';
            });
        }
        return spInvoices.filter(item => item.status === selectedStatus);
    }, [spInvoices, selectedStatus]);

    const handleDelete = useCallback((id) => {
        setSelectedRowId(id);
        deleteMutation.mutate(id);
    }, [deleteMutation]);


    const getFileURLByName = async (fileName) => {
        try {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/file/base64`,
                { fileName }
            );
            return response.split(',')[1];
        } catch (error) {
            if (error.response?.status === 403) {
                showToast("אין לך הרשאה לצפות בחשבונית זו", "error");
            } else if (error.response?.status === 404) {
                showToast("החשבונית לא נמצאה", "error");
            }

            throw error;
        }
    };

    const {
        data: fileAsBase64,
        isLoading: isLoadingFile,
        error: errorFile,
    } = useQuery({
        queryKey: ['fileAsBase64', selectedFileName],
        queryFn: () => selectedFileName ? getFileURLByName(selectedFileName) : null,
        enabled: !!selectedFileName,
        staleTime: FILE_STALE_TIME_MS,
        gcTime: FILE_GC_TIME_MS,
    });

    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    }, [queryClient]);


    return {
        viewAll,
        setViewAll,
        canViewAll,
        spInvoices,
        isLoading,
        isLoadingBackground,
        error,
        deleteMutation,
        showDeleteComp,
        openDelete,
        selectedRowId,
        setOpenDelete,
        handleDelete,
        fileAsBase64,
        isLoadingFile,
        errorFile,
        selectedFileName,
        setSelectedFileName,
        selectedStatus,
        setSelectedStatus,
        statuses,
        filteredspInvoices,
        refreshData,
        updateMutation,
        checkGoodsReceiptNow,
        showToast,
        saveNotesMutation,
        approveMutation,
        rejectMutation,
        handleSaveInvoiceNotes,
        handleApproveInvoiceFromPopup,
        handleRejectInvoiceFromPopup,
        handleRowClick,
        fetchApprovalPackageData,
        initiateApproval,
        completeApprovalWithAllocationKey,
        allocationKeyModalOpen,
        setAllocationKeyModalOpen,
        allocationKeys,
        isLoadingAllocationKeys,
        allocationKeysError,
        currentInvoiceYear,
        isInvoicePriceHigher,
        fetchAllocationKeys,
        fetchAccountantDisplayName,
        getAccountantDisplayName,
        fetchMultipleAccountantNames,
        accountantNamesCache: accountantNamesCacheRef.current,
        retryApprovedInvoiceToPriority,
        retryRequiresAttentionInvoiceToPriority,
        changeInvoiceStatus,
        reprocessRequiresAttentionInvoice,
        setActiveInvoicesData
    };
};
export default useSpInvoices;